import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Typography, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomPagination from '../../../../components/forms/Pagination';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { Item } from '../../../../app/services/api/orderManagementClient';
import NoProductImage from '../../../../assets/images/NoProductImage.png';
import RoundAndFormatValue from '../../../../components/shared/RoundAndFormatValue';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { ItemTagChip } from '../../ItemTags/components/ItemTagChip';

interface CataloguesTableProps {
  catalogueId: string | undefined;
  catalogueName: string | undefined;
  list: Item[];
  isLoading: boolean;
  searchTerm: string;
  clearAll: () => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  canViewDetails: boolean;
  canViewItemTags: boolean;
}

const CatalogueProductsTable = ({ catalogueId, catalogueName, list, isLoading, searchTerm, clearAll, page, setPage, totalPages, canViewDetails, canViewItemTags }: CataloguesTableProps) => {
  const { t } = useTranslation('catalogues');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [openOverflowForSku, setOpenOverflowForSku] = useState<string | null>(null);

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  if (!isLoading && list.length === 0) {
    return <EmptyTable
      text={t('products')}
      searchTerm={searchTerm}
      onClear={clearAll}
      onAdd={() => { }}
      addItemText={t('newProduct')}
      onAddIsCreating={false} />;
  }

  return (
    <>
      <TableContainer sx={{ borderRadius: '12px' }}
        component='div'>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
            <TableRow>
              <TableCell>
                {t('details.image')}
              </TableCell>
              <TableCell>
                {t('details.productId')}
              </TableCell>
              <TableCell>
                {t('details.internalId')}
              </TableCell>
              <TableCell>
                {t('details.name')}
              </TableCell>
              <TableCell>
                {t('details.quantityInUOM')}
              </TableCell>
              <TableCell>
                {t('details.uom')}
              </TableCell>
              <TableCell>
                {t('details.price')}
              </TableCell>
              <TableCell>
                {t('details.category')}
              </TableCell>
              <TableCell>
                {t('details.attributesStatus')}
              </TableCell>
              <TableCell>
                {t('details.documentsStatus')}
              </TableCell>
              {canViewItemTags && (
                <TableCell>
                  {t('details.tags')}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row, index) => (
              <TableRow
                key={`${index}${row.name}`}
                onClick={() => canViewDetails ? navigate(`/ofd/products/${row.sku}`, { state: { catalogueId: catalogueId, catalogueName: catalogueName } }) : undefined}
                sx={{ cursor: canViewDetails ? 'pointer' : 'default' }}
              >
                <TableCell>
                  <Box
                    style={{
                      width: '100px',
                      height: isMobile ? '100px' : '75px',
                      justifyContent: 'center',
                      backgroundColor: (row?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #ddd',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'contain',
                      imageRendering: '-webkit-optimize-contrast',
                      backgroundImage: `url(${row.thumbnailUrl && row.thumbnailUrl.trim() !== '' ? row.thumbnailUrl : NoProductImage})`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'
                    fontWeight={600}
                    color={theme.palette.primary[500]}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: canViewDetails ? 'underline' : 'none' } }}
                  >
                    {row.sku}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.internalId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.quantityInUom}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.orderableUom}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex"
                    flexDirection="column">
                    <Typography variant='textXS'>
                      <RoundAndFormatValue value={row.price ?? 0}
                        currencyCode={currencyCode} />
                    </Typography>
                    {row.gbpPrice !== undefined && row.price !== row.gbpPrice && (
                      <Typography
                        variant='textXS'
                        sx={{
                          color: theme.palette.text.disabled,
                          fontStyle: 'italic',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                        }}
                      >
        ({<RoundAndFormatValue value={row.gbpPrice}
                          currencyCode="GBP" />})
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.category}
                  </Typography>
                </TableCell>
                <TableCell>
                  {(row.missingRequiredAttributes && row.missingRequiredAttributes.length > 0) ? (
                    <Tooltip
                      title={`Missing attributes: ${row.missingRequiredAttributes.join(', ')}`}
                      arrow
                    >
                      <ErrorOutlineIcon
                        sx={{ color: theme.palette.warning.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="No missing attributes"
                      arrow
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: theme.palette.success.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {(row.missingRequiredDocuments && row.missingRequiredDocuments.length > 0) ? (
                    <Tooltip
                      title={`Missing documents: ${row.missingRequiredDocuments.join(', ')}`}
                      arrow
                    >
                      <ErrorOutlineIcon
                        sx={{ color: theme.palette.warning.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="No missing documents"
                      arrow
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: theme.palette.success.main, fontSize: '16px' }}
                      />
                    </Tooltip>
                  )}                
                </TableCell>
                {canViewItemTags && (
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.55, alignItems: 'flex-start' }}>
                        {(row.itemTags ?? []).slice(0, 2).map((tag) => (
                          <Box
                            key={tag.id}
                            sx={{ display: 'inline-flex', alignItems: 'center', mb: '10px' }}
                          >
                            <ItemTagChip
                              name={tag.name}
                              textColor={tag.textColor}
                              backgroundColor={tag.backgroundColor}
                              iconKey={tag.iconKey}
                              isActive={true}
                              placeholderLabel=''
                              scale={0.9}
                            />
                          </Box>
                        ))}
                      </Box>
                      {(row.itemTags?.length ?? 0) > 2 && (
                        <Tooltip
                          open={openOverflowForSku === row.sku}
                          onClose={() => setOpenOverflowForSku(null)}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          title={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, p: 0.5 }}>
                              {row.itemTags!.slice(2).map((tag) => (
                                <Box
                                  key={tag.id}
                                  sx={{ display: 'inline-flex', alignItems: 'center' }}
                                >
                                  <ItemTagChip
                                    name={tag.name}
                                    textColor={tag.textColor}
                                    backgroundColor={tag.backgroundColor}
                                    iconKey={tag.iconKey}
                                    isActive={true}
                                    placeholderLabel=''
                                    scale={0.9}

                                  />
                                </Box>
                              ))}
                            </Box>
                          }
                          arrow
                          placement='top'
                        >
                          <Box
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenOverflowForSku((current) => current === row.sku ? null : row.sku);
                            }}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 24,
                              minWidth: 24,
                              px: 1,
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              backgroundColor: theme.palette.custom.gray[200],
                              color: theme.palette.custom.gray[700],
                              border: `1px solid ${theme.palette.custom.gray[300]}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {`+${(row.itemTags?.length ?? 0) - 2}`}
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {/* Add empty rows to maintain height */}
            {Array.from({ length: Math.max(0, 10 - (list.length ?? 0)) }).map((_, index) => (
              <TableRow key={`empty-row-${index}`}
                sx={{ height: '40px', backgroundColor: theme.palette.common.white }}>
                <TableCell colSpan={canViewItemTags ? 11 : 10}
                  sx={{
                    backgroundColor: theme.palette.common.white,
                    borderColor: index === Math.max(0, 10 - (list.length ?? 0)) - 1 ? theme.palette.custom.gray[100] : theme.palette.common.white,
                  }} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={totalPages}
        breakpointForChangeDisplay={120000}
        disabled={isLoading}
      />
    </>
  );
};

export default CatalogueProductsTable;
